/* Brilinton Surface Manager — R44-271
 * One owner for top-level surface arbitration, viewport locking, scroll restoration,
 * and trigger-focus restoration. Visual motion remains component-owned.
 */
(() => {
  'use strict';

  if (window.BrilintonSurfaceManager) return;

  const records = new Map();
  const openOrder = [];
  const lockOwners = new Map();
  const suppressedFocus = new Set();
  let savedScrollY = 0;
  let savedInlineBody = null;
  let savedBodyPaddingRight = null;
  let bodyFixed = false;

  const connected = (node) => Boolean(node && node.isConnected);

  function applyScrollbarCompensation() {
    if (savedBodyPaddingRight !== null) return;
    savedBodyPaddingRight = document.body.style.paddingRight;
    const gap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    document.documentElement.style.setProperty('--br-surface-scrollbar-gap', `${gap}px`);
    if (!gap) return;
    const computedPadding = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
    document.body.style.paddingRight = `${computedPadding + gap}px`;
  }

  function restoreScrollbarCompensation() {
    if (savedBodyPaddingRight === null) return;
    document.body.style.paddingRight = savedBodyPaddingRight;
    savedBodyPaddingRight = null;
    document.documentElement.style.removeProperty('--br-surface-scrollbar-gap');
  }

  function snapshotBodyInlineStyles() {
    if (savedInlineBody) return;
    savedInlineBody = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
    };
  }

  function restoreBodyInlineStyles() {
    if (!savedInlineBody) return;
    document.body.style.position = savedInlineBody.position;
    document.body.style.top = savedInlineBody.top;
    document.body.style.left = savedInlineBody.left;
    document.body.style.right = savedInlineBody.right;
    document.body.style.width = savedInlineBody.width;
    savedInlineBody = null;
  }

  function applyFixedLock() {
    if (bodyFixed) return;
    snapshotBodyInlineStyles();
    document.body.style.position = 'fixed';
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    bodyFixed = true;
  }

  function releaseFixedLock({ restoreScroll = true } = {}) {
    if (!bodyFixed) return;
    restoreBodyInlineStyles();
    bodyFixed = false;
    if (restoreScroll) window.scrollTo(0, savedScrollY);
  }

  function reconcileViewportLock() {
    const hasLocks = lockOwners.size > 0;
    const needsFixed = Array.from(lockOwners.values()).some((mode) => mode === 'fixed');

    // Keep the root scroll container untouched. The sticky site header depends on it.
    document.documentElement.classList.remove('br-surface-locked');
    document.body.classList.toggle('br-surface-locked', hasLocks);

    if (!hasLocks) {
      releaseFixedLock({ restoreScroll: true });
      restoreScrollbarCompensation();
      const currentY = window.scrollY || document.documentElement.scrollTop || 0;
      if (Math.abs(currentY - savedScrollY) > 1) window.scrollTo(0, savedScrollY);
      return;
    }

    if (needsFixed) {
      applyFixedLock();
    } else if (bodyFixed) {
      releaseFixedLock({ restoreScroll: true });
      document.body.classList.add('br-surface-locked');
    }
  }

  function acquireLock(name, mode = 'overflow') {
    if (!lockOwners.size) {
      savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
      applyScrollbarCompensation();
    }
    lockOwners.set(name, mode === 'fixed' ? 'fixed' : 'overflow');
    reconcileViewportLock();
  }

  function releaseLock(name) {
    lockOwners.delete(name);
    reconcileViewportLock();
  }

  function removeFromOrder(name) {
    let index = openOrder.lastIndexOf(name);
    while (index !== -1) {
      openOrder.splice(index, 1);
      index = openOrder.lastIndexOf(name);
    }
  }

  function restoreFocus(name) {
    if (suppressedFocus.delete(name)) return;
    const record = records.get(name);
    const trigger = record && record.trigger;
    if (!connected(trigger) || typeof trigger.focus !== 'function') return;
    requestAnimationFrame(() => {
      if (connected(trigger)) trigger.focus({ preventScroll: true });
    });
  }

  function closeRegistered(name, reason = 'handoff') {
    const record = records.get(name);
    if (!record || !record.open) return false;

    if (reason === 'handoff') suppressedFocus.add(name);
    if (typeof record.close === 'function') {
      record.close({ reason });
    } else {
      api.close(name, { restoreFocus: reason !== 'handoff' });
    }
    return true;
  }

  const api = {
    register(name, options = {}) {
      if (!name) return;
      const current = records.get(name) || {};
      records.set(name, {
        ...current,
        ...options,
        name,
        topLevel: options.topLevel !== false,
        open: Boolean(current.open),
        trigger: current.trigger || null,
      });
    },

    unregister(name) {
      const record = records.get(name);
      if (record && record.open) api.close(name, { restoreFocus: false });
      records.delete(name);
      suppressedFocus.delete(name);
      removeFromOrder(name);
    },

    open(name, options = {}) {
      if (!name) return;
      if (!records.has(name)) api.register(name, options);
      const record = records.get(name);
      if (options.close) record.close = options.close;
      if (options.root) record.root = options.root;
      if (options.topLevel !== undefined) record.topLevel = options.topLevel !== false;
      if (options.trigger) record.trigger = options.trigger;
      if (options.lockMode) record.lockMode = options.lockMode;
      if (options.preservePeers) record.preservePeers = Array.from(options.preservePeers);

      const preservedPeers = new Set(record.preservePeers || []);
      const shouldClosePeers = options.closePeers !== false && record.topLevel !== false;
      const peers = shouldClosePeers
        ? openOrder.filter((otherName) => {
            if (otherName === name || preservedPeers.has(otherName)) return false;
            const other = records.get(otherName);
            return Boolean(other && other.open && other.topLevel !== false);
          })
        : [];

      record.open = true;
      removeFromOrder(name);
      openOrder.push(name);
      acquireLock(name, record.lockMode || options.lockMode || 'overflow');

      peers.reverse().forEach((peer) => closeRegistered(peer, 'handoff'));
      document.documentElement.dataset.brSurfaceTop = name;
      document.dispatchEvent(new CustomEvent('brilinton:surface-open', { detail: { name, peers } }));
    },

    close(name, options = {}) {
      const record = records.get(name);
      if (!record || !record.open) {
        releaseLock(name);
        return;
      }

      record.open = false;
      removeFromOrder(name);
      releaseLock(name);

      const top = api.top();
      if (top) document.documentElement.dataset.brSurfaceTop = top;
      else document.documentElement.removeAttribute('data-br-surface-top');

      if (options.restoreFocus !== false) restoreFocus(name);
      else suppressedFocus.delete(name);
      document.dispatchEvent(new CustomEvent('brilinton:surface-close', { detail: { name, top } }));
    },

    setLockMode(name, mode) {
      const record = records.get(name);
      if (record) record.lockMode = mode === 'fixed' ? 'fixed' : 'overflow';
      if (record && record.open) acquireLock(name, record.lockMode);
    },

    isOpen(name) {
      return Boolean(records.get(name)?.open);
    },

    top() {
      for (let index = openOrder.length - 1; index >= 0; index -= 1) {
        const name = openOrder[index];
        if (records.get(name)?.open) return name;
      }
      return null;
    },

    closeTop(reason = 'header') {
      const name = api.top();
      if (!name) return false;
      return closeRegistered(name, reason);
    },

    acquireLock,
    releaseLock,

    state() {
      return {
        top: api.top(),
        open: openOrder.filter((name) => records.get(name)?.open),
        locks: Array.from(lockOwners.entries()),
      };
    },
  };

  window.BrilintonSurfaceManager = api;
})();
